import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Divider,
  IconButton,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';

interface PDFGeneratorConfigPanelProps {
  nodeId: string;
  nodeData: any;
  onSave: (nodeId: string, data: any) => void;
  onClose: () => void;
}

const PDFGeneratorConfigPanel: React.FC<PDFGeneratorConfigPanelProps> = ({
  nodeId,
  nodeData,
  onSave,
  onClose,
}) => {
  const [title, setTitle] = useState(nodeData?.title || 'Generated Document');
  const [filename, setFilename] = useState(nodeData?.filename || 'output.pdf');
  const [enableMarkdown, setEnableMarkdown] = useState(nodeData?.enableMarkdown !== false);

  useEffect(() => {
    setTitle(nodeData?.title || 'Generated Document');
    setFilename(nodeData?.filename || 'output.pdf');
    setEnableMarkdown(nodeData?.enableMarkdown !== false);
  }, [nodeData]);

  const handleSave = () => {
    onSave(nodeId, {
      ...nodeData,
      title,
      filename,
      enableMarkdown,
    });
    onClose();
  };

  return (
    <Paper
      sx={{
        position: 'absolute',
        right: 16,
        top: 16,
        width: 350,
        maxHeight: '90vh',
        overflowY: 'auto',
        zIndex: 1000,
        boxShadow: 3,
      }}
    >
      <Box sx={{ p: 2, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            PDF Generator Configuration
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />
      </Box>

      <Box sx={{ px: 2, pb: 2 }}>
        <TextField
          fullWidth
          label="PDF Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{ mb: 2 }}
          helperText="Title that appears in the PDF document"
        />

        <TextField
          fullWidth
          label="Filename"
          value={filename}
          onChange={(e) => setFilename(e.target.value)}
          sx={{ mb: 2 }}
          helperText="Output filename (e.g., report.pdf)"
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={enableMarkdown}
              onChange={(e) => setEnableMarkdown(e.target.checked)}
            />
          }
          label="Enable Markdown Rendering"
          sx={{ mb: 2 }}
        />
        {enableMarkdown && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1, mb: 2, display: 'block' }}>
            Markdown formatting (headers, lists, code blocks, etc.) will be rendered in the PDF
          </Typography>
        )}

        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
          >
            Save
          </Button>
          <Button
            fullWidth
            variant="outlined"
            onClick={onClose}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default PDFGeneratorConfigPanel;

